import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { X, ClipboardList, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { serviceCallsAPI } from '@shared/apiClient';
import { formatDate, formatCoolingType } from '@shared/utils';
import { Section } from './AssignmentListItems';

const SUPPLY_LABELS = [
  { key: 'generatorType', label: 'Generator Type' },
  { key: 'generatorModel', label: 'Generator Model' },
  { key: 'generatorSize', label: 'Generator Size' },
  { key: 'generatorCoolingType', label: 'Cooling', format: formatCoolingType },
  { key: 'generatorSerialNumber', label: 'Serial Number' },
  { key: 'airFilter', label: 'Air Filter' },
  { key: 'oilFilter1', label: 'Oil Filter 1' },
  { key: 'oilFilter2', label: 'Oil Filter 2' },
  { key: 'fuelFilter1', label: 'Fuel Filter 1' },
  { key: 'fuelFilter2', label: 'Fuel Filter 2' },
  { key: 'fuelType', label: 'Fuel Type' },
  { key: 'fuelAdditive', label: 'Fuel Additive' },
  { key: 'oilType', label: 'Oil Type' },
  { key: 'oilAmount', label: 'Oil Amount' },
  { key: 'batterySize', label: 'Battery Size' }
];

function CategoryCard({ category }) {
  const [expandedValue, setExpandedValue] = useState(null);

  if (!category.items?.length) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
          {category.label}
        </h3>
        <p className="mt-2 text-sm text-slate-400">None needed for this day.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
        {category.label}
      </h3>
      <div className="mt-3 space-y-2">
        {category.items.map((item) => {
          const itemKey = `${category.key}-${item.value}`;
          const isExpanded = expandedValue === itemKey;

          return (
            <div key={itemKey} className="rounded-xl border border-slate-100 bg-slate-50">
              <button
                type="button"
                onClick={() => setExpandedValue(isExpanded ? null : itemKey)}
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900">{item.value}</p>
                  {category.key === 'oil' && item.amounts?.length > 0 && (
                    <p className="mt-0.5 text-xs text-slate-500">
                      {item.totalAmount != null
                        ? `Total: ${item.totalAmount} (${item.amounts.join(', ')})`
                        : item.amounts.join(', ')}
                    </p>
                  )}
                </div>
                <span className="inline-flex flex-shrink-0 items-center rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-700">
                  x{item.count}
                </span>
                {item.clients?.length > 0 && (
                  isExpanded
                    ? <ChevronUp className="h-4 w-4 flex-shrink-0 text-slate-400" />
                    : <ChevronDown className="h-4 w-4 flex-shrink-0 text-slate-400" />
                )}
              </button>
              {isExpanded && item.clients?.length > 0 && (
                <div className="border-t border-slate-100 px-3 py-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                    Needed for
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {item.clients.map((clientName) => (
                      <li key={clientName} className="text-sm text-slate-600">
                        {clientName}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CallSuppliesCard({ call }) {
  const [expanded, setExpanded] = useState(false);
  const filledFields = SUPPLY_LABELS.filter(({ key }) => call.supplies?.[key]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-full items-start gap-3 text-left"
      >
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-slate-900">{call.name}</p>
          {call.address && (
            <p className="mt-0.5 text-sm text-slate-500">{call.address}</p>
          )}
          {!call.hasGeneratorInfo ? (
            <p className="mt-1 text-sm font-medium text-amber-600">
              No generator info on file
            </p>
          ) : call.supplies?.generatorModel && (
            <p className="mt-1 text-sm text-slate-600">
              {call.supplies.generatorModel}
              {call.supplies.generatorSize ? ` (${call.supplies.generatorSize})` : ''}
            </p>
          )}
        </div>
        {call.hasGeneratorInfo && (
          expanded
            ? <ChevronUp className="mt-1 h-5 w-5 flex-shrink-0 text-slate-400" />
            : <ChevronDown className="mt-1 h-5 w-5 flex-shrink-0 text-slate-400" />
        )}
      </button>
      {expanded && call.hasGeneratorInfo && (
        <dl className="mt-3 grid gap-2 border-t border-slate-100 pt-3 sm:grid-cols-2">
          {filledFields.map(({ key, label, format }) => (
            <div key={key}>
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">
                {label}
              </dt>
              <dd className="text-sm text-slate-700">
                {format ? format(call.supplies[key]) : call.supplies[key]}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

export default function DailySuppliesModal({ date, isOpen, onClose }) {
  const suppliesQuery = useQuery(
    ['dailySupplies', date],
    () => serviceCallsAPI.getDailySupplies(date),
    { enabled: isOpen && !!date, refetchOnWindowFocus: true }
  );

  const data = suppliesQuery.data?.data;
  const displayDate = useMemo(() => {
    if (!date) return '';
    const [year, month, day] = date.split('-').map(Number);
    return formatDate(new Date(year, month - 1, day), 'EEEE, MMMM d, yyyy');
  }, [date]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-white">
      <div className="flex h-full w-full flex-col bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <div className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-primary-600" />
              <h3 className="text-xl font-semibold text-gray-900">Parts List</h3>
            </div>
            <p className="mt-1 text-sm text-slate-500">{displayDate}</p>
          </div>
          <button type="button" onClick={onClose} className="icon-btn">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {suppliesQuery.isLoading ? (
            <p className="text-sm text-slate-400">Loading parts list…</p>
          ) : suppliesQuery.isError ? (
            <p className="text-sm text-red-600">Could not load the parts list. Try again.</p>
          ) : (
            <div className="space-y-6">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-900">{data?.totalCalls || 0}</span>
                  {' '}
                  {data?.totalCalls === 1 ? 'call' : 'calls'} scheduled
                </p>
                {data?.callsMissingGeneratorInfo?.length > 0 && (
                  <div className="mt-2 flex items-start gap-2 text-sm text-amber-700">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <p>
                      {data.callsMissingGeneratorInfo.length}
                      {' '}
                      {data.callsMissingGeneratorInfo.length === 1 ? 'call has' : 'calls have'}
                      {' '}
                      no generator info on file.
                    </p>
                  </div>
                )}
              </div>

              <div>
                <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-700">
                  Inventory Needed
                </h2>
                <div className="grid gap-3 md:grid-cols-2">
                  {(data?.categories || []).map((category) => (
                    <CategoryCard key={category.key} category={category} />
                  ))}
                </div>
              </div>

              <Section
                title="By Customer"
                icon={ClipboardList}
                count={data?.calls?.length || 0}
                emptyText="No calls scheduled for this day."
              >
                {(data?.calls || []).map((call) => (
                  <CallSuppliesCard key={call.serviceCallId} call={call} />
                ))}
              </Section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
